import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiLock, FiPlus, FiSave, FiSearch, FiClipboard } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite } from '../utilities/adminAccess';
import { extractList, isEnabled } from '../utilities/tournamentsApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';

const isApplicationLocked = (item) => Boolean(item?.locked) || Number(item?.id) === 2;

export default function TournamentApplicationsManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();

  const [applications, setApplications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!champId && champsLoading) return;
    loadApplications();
  }, [champId, champsLoading]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return applications;
    return applications.filter((item) => (
      String(item.name || '').toLowerCase().includes(q)
      || String(item.year || '').toLowerCase().includes(q)
    ));
  }, [applications, searchQuery]);

  const loadApplications = async () => {
    if (!champId) {
      setApplications([]);
      setOriginalOrder([]);
      setLoading(false);
      setMessage({ text: t('championshipsLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminApplications, {
        champ_id: champId,
        championat_id: champId,
      });
      const list = extractList(response.data);
      setApplications(list);
      setOriginalOrder(list);
      setReorderMode(false);
    } catch (error) {
      setApplications([]);
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.changeApplicationsPriority, {
        applications: applications.map((item, index) => ({ id: item.id, priority: index })),
      });
      const ordered = applications.map((item, index) => ({ ...item, priority: index }));
      setApplications(ordered);
      setOriginalOrder(ordered);
      setReorderMode(false);
      setMessage({ text: t('stOrderSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stOrderError')), type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  const columns = [
    {
      key: 'active',
      label: t('stStatus'),
      className: 'st-cell-status',
      render: (item) => (
        <span className={`st-badge ${isEnabled(item.active) ? 'enabled' : 'disabled'}`}>
          <span className="tr-btn-full">
            {isEnabled(item.active) ? t('trApplicationStatusActive') : t('trApplicationStatusInactive')}
          </span>
          <span className="tr-btn-short">
            {isEnabled(item.active) ? t('trApplicationStatusActiveShort') : t('trApplicationStatusInactiveShort')}
          </span>
        </span>
      ),
    },
    {
      key: 'name',
      label: t('stdName'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="tr-app-main">
          <span className="tr-app-name-row">
            {isApplicationLocked(item) ? (
              <FiLock className="tr-app-lock" size={14} title={t('trApplicationLocked')} aria-label={t('trApplicationLocked')} />
            ) : null}
            <span className="tr-app-name">{item.name || '—'}</span>
          </span>
          {item.year ? <small className="tr-app-meta">{item.year}</small> : null}
        </span>
      ),
    },
    {
      key: 'year',
      label: t('trApplicationYear'),
      className: 'st-cell-muted tr-cell-year',
      render: (item) => item.year || '—',
    },
    {
      key: 'application_limit',
      label: t('trApplicationLimit'),
      className: 'st-cell-muted tr-cell-limit',
      render: (item) => (
        item.application_limit == null || item.application_limit === ''
          ? '—'
          : Number(item.application_limit) < 0
            ? '∞'
            : String(item.application_limit)
      ),
    },
    {
      key: 'through_limit',
      label: t('trApplicationThroughLimitShort'),
      className: 'st-cell-muted tr-cell-through',
      render: (item) => (
        item.through_limit == null || item.through_limit === ''
          ? '15'
          : String(item.through_limit)
      ),
    },
    {
      key: 'professional_limit',
      label: t('trApplicationProfessionalLimitShort'),
      className: 'st-cell-muted tr-cell-pro-limit',
      render: (item) => (
        item.professional_unlimited
        || item.professional_limit == null
        || item.professional_limit === ''
          ? '∞'
          : String(item.professional_limit)
      ),
    },
    {
      key: 'priority',
      label: t('stdPriority'),
      className: 'st-cell-priority',
      render: (item, index) => (
        <span className="st-priority-badge">{reorderMode ? index + 1 : (item.priority ?? index + 1)}</span>
      ),
    },
  ];

  const visibleItems = reorderMode ? applications : filtered;

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tr-apps-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('navTournaments'), to: '/tournaments' },
                { label: t('trApplications') },
              ]}
            />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('trApplicationSearchPh')}
              value={searchQuery}
              disabled={reorderMode}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          <div className="st-meta">
            <strong>{t('stFound', { count: visibleItems.length })}</strong>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-table-wrap">
            {loading ? (
              <div className="st-loading">
                <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                <p>{t('trLoading')}</p>
              </div>
            ) : (
              <ReorderableTable
                tableClassName="st-table-tr-apps"
                columns={columns}
                items={visibleItems}
                getRowKey={(item) => item.id}
                reorderMode={reorderMode}
                onItemsChange={setApplications}
                isRowLocked={isApplicationLocked}
                getRowHref={(item) => (
                  isApplicationLocked(item) || !item.id
                    ? null
                    : `/tournaments/applications/${item.id}`
                )}
                empty={(
                  <div className="st-empty">
                    <FiClipboard size={48} />
                    <h3>{t('trEmptyApplications')}</h3>
                    <p>{t('trEmptyApplicationsHint')}</p>
                  </div>
                )}
              />
            )}
            {reorderMode ? <p className="st-hint">{t('stDragHint')}</p> : null}
          </div>
        </div>

        <div className="st-actions">
          {reorderMode ? (
            <>
              <button
                type="button"
                className="st-btn st-btn-secondary"
                disabled={savingOrder}
                onClick={() => {
                  setApplications(originalOrder);
                  setReorderMode(false);
                }}
              >
                {t('stCancelReorder')}
              </button>
              <button
                type="button"
                className="st-btn st-btn-primary"
                disabled={savingOrder || !applications.length}
                onClick={handleSaveOrder}
              >
                <FiSave /> {savingOrder ? t('trLoading') : t('stSaveOrder')}
              </button>
            </>
          ) : (
            <IfCanWrite entity="tournaments">
              <button
                type="button"
                className="st-btn st-btn-secondary"
                disabled={loading || applications.length < 2 || Boolean(searchQuery.trim())}
                onClick={() => {
                  setOriginalOrder(applications);
                  setReorderMode(true);
                  setMessage({ text: '', type: '' });
                }}
              >
                {t('stReorder')}
              </button>
              <Link to="/tournaments/applications/new" className="st-btn st-btn-primary">
                <FiPlus /> {t('trAddApplication')}
              </Link>
            </IfCanWrite>
          )}
        </div>
      </div>
    </div>
  );
}
